import { RowCrudStatus } from "../../common/types";
import {
  TenantProfile,
  TenantProfileCreateMutationVariables,
  TenantProfileUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { PersonType } from "../../../api/graphql";
import { blankToNull } from "../../../common/blankToNull";
import { publisherDisplayId } from "../../publisher/store/PublisherCommonStore";
import { countryDisplayId } from "../../country/store/CountryCommonStore";
import { provinceStateDisplayId } from "../../provinceState/store/ProvinceStateCommonStore";
import { vatStatusDisplayId } from "../../vatStatus/store/VatStatusCommonStore";
import { grossReceiptsStatusDisplayId } from "../../grossReceiptsStatus/store/GrossReceiptsStatusCommonStore";
import { currencyDisplayId } from "../../currency/store/CurrencyCommonStore";

export type TenantProfileValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  personType: PersonType | "";
  legalName: string;
  __ownPublisher: string;
  showOwnPublisher: { displayId: string; id: string } | null;
  businessId: string;
  legalEmail: string;
  streetAndNumber: string;
  floor: string;
  suite: string;
  postalCode: string;
  __country: string;
  showCountry: { displayId: string; id: string } | null;
  __province: string;
  showProvince: { displayId: string; id: string } | null;
  city: string;
  __vatStatus: string;
  showVatStatus: { displayId: string; id: string } | null;
  taxId: string;
  __grossReceiptsStatus: string;
  showGrossReceiptsStatus: { displayId: string; id: string } | null;
  grossReceiptsId: string;
  billingEmail: string;
  __billingCurrency: string;
  showBillingCurrency: { displayId: string; id: string } | null;
  printerName: string;
  printerStreetAndNumber: string;
  printerFloor: string;
  printerSuite: string;
  printerPostalCode: string;
  __printerCountry: string;
  showPrinterCountry: { displayId: string; id: string } | null;
  __printerProvince: string;
  showPrinterProvince: { displayId: string; id: string } | null;
  printerCity: string;
  sadaicFtpUrl: string;
  sadaicFtpUser: string;
  sadaicFtpPassword: string;
  sadaicFtpPasswordHasValue: boolean;
  sadaicEmail: string;
};

export type TenantProfileErrorsType = {
  personType?: { message: string; fieldLabel: string };
  legalName?: { message: string; fieldLabel: string };
  ownPublisher?: { message: string; fieldLabel: string };
  businessId?: { message: string; fieldLabel: string };
  legalEmail?: { message: string; fieldLabel: string };
  streetAndNumber?: { message: string; fieldLabel: string };
  floor?: { message: string; fieldLabel: string };
  suite?: { message: string; fieldLabel: string };
  postalCode?: { message: string; fieldLabel: string };
  country?: { message: string; fieldLabel: string };
  province?: { message: string; fieldLabel: string };
  city?: { message: string; fieldLabel: string };
  vatStatus?: { message: string; fieldLabel: string };
  taxId?: { message: string; fieldLabel: string };
  grossReceiptsStatus?: { message: string; fieldLabel: string };
  grossReceiptsId?: { message: string; fieldLabel: string };
  billingEmail?: { message: string; fieldLabel: string };
  billingCurrency?: { message: string; fieldLabel: string };
  printerName?: { message: string; fieldLabel: string };
  printerStreetAndNumber?: { message: string; fieldLabel: string };
  printerFloor?: { message: string; fieldLabel: string };
  printerSuite?: { message: string; fieldLabel: string };
  printerPostalCode?: { message: string; fieldLabel: string };
  printerCountry?: { message: string; fieldLabel: string };
  printerProvince?: { message: string; fieldLabel: string };
  printerCity?: { message: string; fieldLabel: string };
  sadaicFtpUrl?: { message: string; fieldLabel: string };
  sadaicFtpUser?: { message: string; fieldLabel: string };
  sadaicFtpPassword?: { message: string; fieldLabel: string };
  sadaicEmail?: { message: string; fieldLabel: string };
};

export function TenantProfileValuesInit(): TenantProfileValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    personType: "" as PersonType | "",
    legalName: "",
    __ownPublisher: "",
    showOwnPublisher: null,
    businessId: "",
    legalEmail: "",
    streetAndNumber: "",
    floor: "",
    suite: "",
    postalCode: "",
    __country: "",
    showCountry: null,
    __province: "",
    showProvince: null,
    city: "",
    __vatStatus: "",
    showVatStatus: null,
    taxId: "",
    __grossReceiptsStatus: "",
    showGrossReceiptsStatus: null,
    grossReceiptsId: "",
    billingEmail: "",
    __billingCurrency: "",
    showBillingCurrency: null,
    printerName: "",
    printerStreetAndNumber: "",
    printerFloor: "",
    printerSuite: "",
    printerPostalCode: "",
    __printerCountry: "",
    showPrinterCountry: null,
    __printerProvince: "",
    showPrinterProvince: null,
    printerCity: "",
    sadaicFtpUrl: "",
    sadaicFtpUser: "",
    sadaicFtpPassword: "",
    sadaicFtpPasswordHasValue: false,
    sadaicEmail: "",
  };
}

export type TenantProfileType = TenantProfile | TenantProfileValuesType;

export function tenantProfileDisplayId(
  source: Partial<TenantProfile>,
  _langStore: LangStore
) {
  return source.legalName ?? _langStore.emptyStringSymbol;
}

export function getTenantProfileValues(
  source: TenantProfileType,
  _langStore: LangStore,
  _duplicated: boolean = false
): TenantProfileValuesType {
  const entityValues = TenantProfileValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : tenantProfileDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.personType = source.personType || ("" as PersonType | "");
  entityValues.legalName = source.legalName || "";
  entityValues.showOwnPublisher =
    "ownPublisher" in source && source.ownPublisher
      ? {
          id: source.ownPublisher.id,
          displayId: publisherDisplayId(source.ownPublisher, _langStore),
        }
      : "showOwnPublisher" in source && source.showOwnPublisher
      ? {
          id: source.showOwnPublisher.id,
          displayId: source.showOwnPublisher.displayId,
        }
      : null;
  entityValues.__ownPublisher = entityValues.showOwnPublisher?.displayId || "";
  entityValues.businessId = source.businessId || "";
  entityValues.legalEmail = source.legalEmail || "";
  entityValues.streetAndNumber = source.streetAndNumber || "";
  entityValues.floor = source.floor || "";
  entityValues.suite = source.suite || "";
  entityValues.postalCode = source.postalCode || "";
  entityValues.showCountry =
    "country" in source && source.country
      ? {
          id: source.country.id,
          displayId: countryDisplayId(source.country, _langStore),
        }
      : "showCountry" in source && source.showCountry
      ? { id: source.showCountry.id, displayId: source.showCountry.displayId }
      : null;
  entityValues.__country = entityValues.showCountry?.displayId || "";
  entityValues.showProvince =
    "province" in source && source.province
      ? {
          id: source.province.id,
          displayId: provinceStateDisplayId(source.province, _langStore),
        }
      : "showProvince" in source && source.showProvince
      ? { id: source.showProvince.id, displayId: source.showProvince.displayId }
      : null;
  entityValues.__province = entityValues.showProvince?.displayId || "";
  entityValues.city = source.city || "";
  entityValues.showVatStatus =
    "vatStatus" in source && source.vatStatus
      ? {
          id: source.vatStatus.id,
          displayId: vatStatusDisplayId(source.vatStatus, _langStore),
        }
      : "showVatStatus" in source && source.showVatStatus
      ? {
          id: source.showVatStatus.id,
          displayId: source.showVatStatus.displayId,
        }
      : null;
  entityValues.__vatStatus = entityValues.showVatStatus?.displayId || "";
  entityValues.taxId = source.taxId || "";
  entityValues.showGrossReceiptsStatus =
    "grossReceiptsStatus" in source && source.grossReceiptsStatus
      ? {
          id: source.grossReceiptsStatus.id,
          displayId: grossReceiptsStatusDisplayId(
            source.grossReceiptsStatus,
            _langStore
          ),
        }
      : "showGrossReceiptsStatus" in source && source.showGrossReceiptsStatus
      ? {
          id: source.showGrossReceiptsStatus.id,
          displayId: source.showGrossReceiptsStatus.displayId,
        }
      : null;
  entityValues.__grossReceiptsStatus =
    entityValues.showGrossReceiptsStatus?.displayId || "";
  entityValues.grossReceiptsId = source.grossReceiptsId || "";
  entityValues.billingEmail = source.billingEmail || "";
  entityValues.showBillingCurrency =
    "billingCurrency" in source && source.billingCurrency
      ? {
          id: source.billingCurrency.id,
          displayId: currencyDisplayId(source.billingCurrency, _langStore),
        }
      : "showBillingCurrency" in source && source.showBillingCurrency
      ? {
          id: source.showBillingCurrency.id,
          displayId: source.showBillingCurrency.displayId,
        }
      : null;
  entityValues.__billingCurrency =
    entityValues.showBillingCurrency?.displayId || "";
  entityValues.printerName = source.printerName || "";
  entityValues.printerStreetAndNumber = source.printerStreetAndNumber || "";
  entityValues.printerFloor = source.printerFloor || "";
  entityValues.printerSuite = source.printerSuite || "";
  entityValues.printerPostalCode = source.printerPostalCode || "";
  entityValues.showPrinterCountry =
    "printerCountry" in source && source.printerCountry
      ? {
          id: source.printerCountry.id,
          displayId: countryDisplayId(source.printerCountry, _langStore),
        }
      : "showPrinterCountry" in source && source.showPrinterCountry
      ? {
          id: source.showPrinterCountry.id,
          displayId: source.showPrinterCountry.displayId,
        }
      : null;
  entityValues.__printerCountry =
    entityValues.showPrinterCountry?.displayId || "";
  entityValues.showPrinterProvince =
    "printerProvince" in source && source.printerProvince
      ? {
          id: source.printerProvince.id,
          displayId: provinceStateDisplayId(source.printerProvince, _langStore),
        }
      : "showPrinterProvince" in source && source.showPrinterProvince
      ? {
          id: source.showPrinterProvince.id,
          displayId: source.showPrinterProvince.displayId,
        }
      : null;
  entityValues.__printerProvince =
    entityValues.showPrinterProvince?.displayId || "";
  entityValues.printerCity = source.printerCity || "";
  entityValues.sadaicFtpUrl = source.sadaicFtpUrl || "";
  entityValues.sadaicFtpUser = source.sadaicFtpUser || "";
  entityValues.sadaicFtpPassword = "";
  entityValues.sadaicFtpPasswordHasValue = !!source.sadaicFtpPasswordHasValue;
  entityValues.sadaicEmail = source.sadaicEmail || "";
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getTenantProfileUpdateVariables(
  values: TenantProfileValuesType
): TenantProfileUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    personType: values.personType || null,
    legalName: blankToNull(values.legalName),
    ownPublisherId: values.showOwnPublisher?.id || "",
    businessId: blankToNull(values.businessId),
    legalEmail: blankToNull(values.legalEmail),
    streetAndNumber: blankToNull(values.streetAndNumber),
    floor: blankToNull(values.floor),
    suite: blankToNull(values.suite),
    postalCode: blankToNull(values.postalCode),
    countryId: values.showCountry?.id || "",
    provinceId: values.showProvince?.id || "",
    city: blankToNull(values.city),
    vatStatusId: values.showVatStatus?.id || "",
    taxId: blankToNull(values.taxId),
    grossReceiptsStatusId: values.showGrossReceiptsStatus?.id || "",
    grossReceiptsId: blankToNull(values.grossReceiptsId),
    billingEmail: blankToNull(values.billingEmail),
    billingCurrencyId: values.showBillingCurrency?.id || "",
    printerName: blankToNull(values.printerName),
    printerStreetAndNumber: blankToNull(values.printerStreetAndNumber),
    printerFloor: blankToNull(values.printerFloor),
    printerSuite: blankToNull(values.printerSuite),
    printerPostalCode: blankToNull(values.printerPostalCode),
    printerCountryId: values.showPrinterCountry?.id || "",
    printerProvinceId: values.showPrinterProvince?.id || "",
    printerCity: blankToNull(values.printerCity),
    sadaicFtpUrl: blankToNull(values.sadaicFtpUrl),
    sadaicFtpUser: blankToNull(values.sadaicFtpUser),
    sadaicFtpPassword: values.sadaicFtpPassword || undefined,
    sadaicEmail: blankToNull(values.sadaicEmail),
  };
}

export function getTenantProfileCreateVariables(
  values: TenantProfileValuesType
): TenantProfileCreateMutationVariables {
  return {
    personType: values.personType || null,
    legalName: blankToNull(values.legalName),
    ownPublisherId: values.showOwnPublisher?.id || "",
    businessId: blankToNull(values.businessId),
    legalEmail: blankToNull(values.legalEmail),
    streetAndNumber: blankToNull(values.streetAndNumber),
    floor: blankToNull(values.floor),
    suite: blankToNull(values.suite),
    postalCode: blankToNull(values.postalCode),
    countryId: values.showCountry?.id || "",
    provinceId: values.showProvince?.id || "",
    city: blankToNull(values.city),
    vatStatusId: values.showVatStatus?.id || "",
    taxId: blankToNull(values.taxId),
    grossReceiptsStatusId: values.showGrossReceiptsStatus?.id || "",
    grossReceiptsId: blankToNull(values.grossReceiptsId),
    billingEmail: blankToNull(values.billingEmail),
    billingCurrencyId: values.showBillingCurrency?.id || "",
    printerName: blankToNull(values.printerName),
    printerStreetAndNumber: blankToNull(values.printerStreetAndNumber),
    printerFloor: blankToNull(values.printerFloor),
    printerSuite: blankToNull(values.printerSuite),
    printerPostalCode: blankToNull(values.printerPostalCode),
    printerCountryId: values.showPrinterCountry?.id || "",
    printerProvinceId: values.showPrinterProvince?.id || "",
    printerCity: blankToNull(values.printerCity),
    sadaicFtpUrl: blankToNull(values.sadaicFtpUrl),
    sadaicFtpUser: blankToNull(values.sadaicFtpUser),
    sadaicFtpPassword: values.sadaicFtpPassword || undefined,
    sadaicEmail: blankToNull(values.sadaicEmail),
  };
}
